import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import MkBookService from '@features/book/services/bookService/_bookService'
import { bookReducer, setBookDraft } from '@book/store'
import { useCountryStateOptions } from '@hooks/useCountryState'
import { useSiteContext } from '@contexts/MkSiteContext'
import { EMkBookStepName } from '@book/types'
import {
  adjustTotalPaymentDueForSirDeselectedProtection,
  updatePointsProtectionCostsShowInSummary,
} from '@book/utils/sirPointsProtectionCharges'
import { MkBookContext, MkBookContextType } from './_context'

export const BookProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const authHeaders = useAuthHeaders()
  const { themeBrand } = useSiteContext()

  const [state, dispatch] = React.useReducer(bookReducer, MkBookService.initialState())

  // Trigger initial countries fetch and keep states reactive to selected country changes.
  // The hook owns API routes/data; we only derive the currently selected country from the book draft.
  const selectedCountry: string = React.useMemo(() => {
    const travelerStep = state.bookDraft?.bookingSteps?.find(
      (s) => s?.name === EMkBookStepName.TRAVELER_INFO || s?.name === EMkBookStepName.MODIFY_TRAVELER
    ) as any
    return travelerStep?.formState?.checkingInGuestCountry || ''
  }, [state.bookDraft])

  // Invoke the hook at the provider level to fetch countries as soon as the initial
  // bookDraft arrives and update states whenever selectedCountry changes.
  const { countries, states } = useCountryStateOptions(selectedCountry)

  // When countries are fetched, inject them into the travelerInfo fieldSet.checkingInGuestCountry.options
  React.useEffect(() => {
    if (!state.bookDraft || !Array.isArray(countries)) return

    const steps = state.bookDraft.bookingSteps || []
    const stepIndex = steps.findIndex((s: any) => s?.name === EMkBookStepName.TRAVELER_INFO)
    if (stepIndex < 0) return

    const travelerStep: any = steps[stepIndex]
    const currentCountryOptions = travelerStep?.fieldSet?.checkingInGuestCountry?.options || []

    const countriesSelectOptions = [
      // { label: '', value: '' }, // <-- prepend empty option
      ...countries.map(({ countryCode, countryName }) => ({
        label: countryName,
        value: countryCode,
      })),
    ]

    // Avoid unnecessary updates to prevent loops
    if (currentCountryOptions === countriesSelectOptions || currentCountryOptions.length === countriesSelectOptions.length) return

    const updatedSteps = steps.map((s: any, idx: number) => {
      if (idx !== stepIndex) return s
      return {
        ...s,
        fieldSet: {
          ...s.fieldSet,
          checkingInGuestCountry: {
            ...s.fieldSet.checkingInGuestCountry,
            options: countriesSelectOptions,
          },
        },
      }
    })

    dispatch(
      setBookDraft({
        ...state.bookDraft,
        bookingSteps: updatedSteps,
      } as any),
    )
  }, [countries, state.bookDraft])

  // When states are fetched, inject them into the travelerInfo fieldSet.checkingInGuestState.options
  React.useEffect(() => {
    if (!state.bookDraft || !Array.isArray(states)) return

    const steps = state.bookDraft.bookingSteps || []
    const stepIndex = steps.findIndex(
      (s: any) => s?.name === EMkBookStepName.TRAVELER_INFO || s?.name === EMkBookStepName.MODIFY_TRAVELER
    )
    if (stepIndex < 0) return

    const travelerStep: any = steps[stepIndex]
    const currentStateOptions = travelerStep?.fieldSet?.checkingInGuestState?.options || []

    const statesSelectOptions = [
      // { label: '', value: '' }, // <-- prepend empty option
      ...states.map(({ name, value }) => ({
        label: name,
        value,
      })),
    ]

    // Avoid unnecessary updates to prevent loops
    if (currentStateOptions === statesSelectOptions || currentStateOptions.length === statesSelectOptions.length) return

    const updatedSteps = steps.map((s: any, idx: number) => {
      if (idx !== stepIndex) return s
      return {
        ...s,
        fieldSet: {
          ...s.fieldSet,
          checkingInGuestState: {
            ...s.fieldSet.checkingInGuestState,
            options: statesSelectOptions,
          },
        },
      }
    })

    dispatch(
      setBookDraft({
        ...state.bookDraft,
        bookingSteps: updatedSteps,
      } as any),
    )
  }, [states, state.bookDraft])

  React.useEffect(() => {
    if (state.timerData) {
      MkBookService.pushBookTimerData(state.timerData)
    } else {
      MkBookService.removeBookTimerData()
    }
  }, [state.timerData])

  React.useEffect(() => {
    if (state.bookDraft) {
      MkBookService.pushBookDraft(state.bookDraft)
    }
  }, [state.bookDraft])

  const togglePointsProtectionCostsShowInSummary = (newValue: boolean) => {
    const paymentCharges = state.bookDraft?.bookSummary?.paymentCharges || []

    const updatedPaymentCharges = updatePointsProtectionCostsShowInSummary(paymentCharges, newValue)

    // If nothing changed, skip dispatch
    if (updatedPaymentCharges === paymentCharges) return

    dispatch(
      setBookDraft({
        ...state.bookDraft,
        bookSummary: {
          ...state.bookDraft?.bookSummary,
          paymentCharges: updatedPaymentCharges,
        },
      } as any),
    )
  }

  React.useEffect(() => {
    const steps = state.bookDraft?.bookingSteps || []

    // Insert other Step's Dependencies, here...

    // CompleteBooking Step Dependencies
    const completeBookingStep = steps.findIndex((s: any) => s?.name === EMkBookStepName.COMPLETE_BOOKING)
    if (completeBookingStep >= 0) {
      // Points Protection Gets Added
      if (state.bookDraft?.bookingSteps?.[completeBookingStep]?.formState?.protection === 'yes') {
        togglePointsProtectionCostsShowInSummary(true)
      }
      // Points Protection Gets Removed
      if (state.bookDraft?.bookingSteps?.[completeBookingStep]?.formState?.protection === '') {
        togglePointsProtectionCostsShowInSummary(false)
      }
    }
  }, [state.bookDraft])

  const bookService = React.useMemo(() => new MkBookService(state, dispatch, navigate, authHeaders), [state, dispatch, navigate, authHeaders])

  const contextValue = React.useMemo(() => {
    const contextProps = bookService.getContextProps<MkBookContextType>()
    const activeStep = bookService.getActiveStep()

    return {
      ...contextProps,
      totalPaymentDue: adjustTotalPaymentDueForSirDeselectedProtection({
        themeBrand,
        activeStep,
        paymentCharges: contextProps.paymentCharges,
        totalPaymentDue: contextProps.totalPaymentDue,
        getCostById: contextProps.getCostById,
      }),
    }
  }, [bookService, themeBrand])

  return <MkBookContext.Provider value={contextValue}>{children}</MkBookContext.Provider>
}
